import React, {useState, useRef, useCallback, useLayoutEffect} from 'react';
import {View, Switch, ScrollView} from 'react-native';

import AppText from './AppText';
import {inputStyles} from '../styles/input';
import {basicStyles} from '../styles/basic';
import ChevronDownSvg from './svgs/ChevronDownSvg';
import {getUserDisplayName} from '../utilities/methods';


function BulkReassignmentPicker({
    userFeeds = {},
    selectedFeedIds = [],
    onSelectionChange,
    label = 'Currently Assigned Tasks',
    organizationUsers = [],
}) {
    const [active, setActive] = useState(false);
    const [expandedUsers, setExpandedUsers] = useState({});
    const wrapperRef = useRef(null);

    const usersWithFeeds = organizationUsers
        .filter(user => {
            const userOrgId = user.user_organization_id?.toString();
            return userFeeds[userOrgId] && userFeeds[userOrgId].length > 0;
        })
        .map(user => {
            const userOrgId = user.user_organization_id.toString();
            return {
                ...user,
                feeds: userFeeds[userOrgId] || [],
            };
        });

    const totalSelected = selectedFeedIds.length;

    const getSelectedLabel = () => {
        if (totalSelected > 0) {
            return totalSelected + ' task' + (totalSelected !== 1 ? 's' : '') + ' selected';
        }
        return 'Select Users or Tasks';
    };

    const isUserFullySelected = (user) => {
        return user.feeds.length > 0 && user.feeds.every(feed =>
            selectedFeedIds.includes(feed.message_feed_id)
        );
    };

    const isUserPartiallySelected = (user) => {
        const selectedCount = user.feeds.filter(feed =>
            selectedFeedIds.includes(feed.message_feed_id)
        ).length;
        return selectedCount > 0 && selectedCount < user.feeds.length;
    };

    const toggleUser = (user) => {
        const feedIds = user.feeds.map(f => f.message_feed_id);
        if (isUserFullySelected(user)) {
            onSelectionChange(selectedFeedIds.filter(id => !feedIds.includes(id)));
        } else {
            const newIds = [...selectedFeedIds];
            feedIds.forEach(id => {
                if (!newIds.includes(id)) {
                    newIds.push(id);
                }
            });
            onSelectionChange(newIds);
        }
    };

    const toggleFeed = (feedId) => {
        if (selectedFeedIds.includes(feedId)) {
            onSelectionChange(selectedFeedIds.filter(id => id !== feedId));
        } else {
            onSelectionChange([...selectedFeedIds, feedId]);
        }
    };

    const toggleExpanded = (userOrgId) => {
        setExpandedUsers(prev => ({
            ...prev,
            [userOrgId]: !prev[userOrgId],
        }));
    };

    const useClickOutside = (node, onClickOutside) => {
        const onClick = useCallback(
            (e) => {
                if (node && !node.contains(e.target)) {
                    onClickOutside();
                }
            },
            [node, onClickOutside],
        );

        useLayoutEffect(() => {
            document.addEventListener('click', onClick, false);
            return () => {
                document.removeEventListener('click', onClick, false);
            };
        }, [onClick]);
    };

    useClickOutside(wrapperRef.current, () => {
        if (active) {
            setActive(false);
        }
    });

    return (
        <View style={[
            inputStyles.inputWrapper,
            {
                paddingRight: 35,
                flex: 1,
                overflow: 'visible',
                zIndex: active ? 999 : 1,
            },
        ]}
              onClick={() => setActive(true)}
              onPress={() => setActive(true)}
        >
            <View>
                <AppText style={inputStyles.inputLabel}>
                    {label}
                </AppText>
                <AppText style={[inputStyles.input, {textWrap: 'nowrap'}]}>
                    {getSelectedLabel()}
                </AppText>
            </View>

            <View style={[
                basicStyles.flexCenterContent,
                {
                    width: 25,
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    borderLeftWidth: 1,
                    borderLeftColor: '#E2E9F4',
                },
            ]}>
                <ChevronDownSvg color="#0065DB"/>
            </View>

            <div
                ref={wrapperRef}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                style={{
                    position: 'absolute',
                    top: '100%',
                    width: '100%',
                    left: 0,
                    display: active && usersWithFeeds.length > 0 ? 'flex' : 'none',
                    flexDirection: 'column',
                }}>
                <View style={basicStyles.contentSection}>
                    <View style={{padding: 15}}>
                        <ScrollView style={{maxHeight: 400}}>
                            {usersWithFeeds.map(user => {
                                const userOrgId = user.user_organization_id.toString();
                                const isExpanded = !!expandedUsers[userOrgId];
                                const fullySelected = isUserFullySelected(user);
                                const partiallySelected = isUserPartiallySelected(user);

                                return (
                                    <View key={userOrgId}>
                                        <View style={[
                                            basicStyles.flexRow,
                                            basicStyles.alignContentCenter,
                                            {width: '100%', marginTop: 5, marginBottom: 5},
                                        ]}>
                                            <View
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleExpanded(userOrgId);
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    marginRight: 8,
                                                    width: 16,
                                                    height: 16,
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    transform: [{rotate: isExpanded ? '0deg' : '-90deg'}],
                                                }}
                                            >
                                                <ChevronDownSvg
                                                    color="#9BA8CA"
                                                    opacity={1}
                                                />
                                            </View>
                                            <Switch
                                                style={{marginRight: 10}}
                                                value={fullySelected || partiallySelected}
                                                onValueChange={() => toggleUser(user)}
                                                trackColor={{
                                                    true: partiallySelected && !fullySelected ? '#99C2ED' : '#0065DB',
                                                    false: '#F2F2F2',
                                                }}
                                                thumbColor="#0065DB"
                                                activeThumbColor="#FFFFFF"
                                            />
                                            <AppText
                                                numberOfLines={1}
                                                style={{fontWeight: '600'}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleExpanded(userOrgId);
                                                }}
                                            >
                                                {getUserDisplayName(user)} ({user.feeds.length})
                                            </AppText>
                                        </View>

                                        {isExpanded && user.feeds.map(feed => (
                                            <View
                                                key={feed.message_feed_id}
                                                style={[
                                                    basicStyles.flexRow,
                                                    basicStyles.alignContentCenter,
                                                    {
                                                        width: '100%',
                                                        marginTop: 3,
                                                        marginBottom: 3,
                                                        paddingLeft: 30,
                                                    },
                                                ]}
                                            >
                                                <Switch
                                                    style={{marginRight: 10}}
                                                    value={selectedFeedIds.includes(feed.message_feed_id)}
                                                    onValueChange={() => toggleFeed(feed.message_feed_id)}
                                                    trackColor={{
                                                        true: '#0065DB',
                                                        false: '#F2F2F2',
                                                    }}
                                                    thumbColor="#0065DB"
                                                    activeThumbColor="#FFFFFF"
                                                />
                                                <AppText numberOfLines={1}>
                                                    {feed.message_feed_contact_name || 'Unknown Contact'}
                                                </AppText>
                                            </View>
                                        ))}
                                    </View>
                                );
                            })}
                        </ScrollView>
                    </View>
                </View>
            </div>
        </View>
    );
}

export default BulkReassignmentPicker;
